// Module: positionsRegistry.js
// Purpose: THE sole accessor for the shared `positionsRef` Map. Every reader/writer of a
//   position row goes through this module — a direct positionsRef.current.{get,set,delete,has}
//   call outside this file is a grep-gate violation (see positionsRegistry.accessorGate.test.js
//   and 02-design.md §D8 / §Test strategy). The wrapper deliberately does NOT expose
//   .get/.set/.delete/.has, so any un-migrated raw-Map call site fails LOUD (a TypeError caught
//   by any test that exercises the path) rather than silently reading/writing the wrong key.

/**
 * Never emit the literal "undefined"/"null" inside a composite key — degrade honestly
 * (D9's own mandate; BD position rows carry instrument_id=None, consolidated_view.py:220).
 * EXPORTED (not module-private) — D9's `groupOrdersIntoTrades` openSids/newestEntryIdBySid
 * rekey imports this SAME function, so the registry's key and the pairing engine's key can
 * never diverge on how they degrade a missing instrument_id.
 * @param {*} val
 * @param {string} fallbackToken
 * @returns {string}
 */
export function degradeIdentityPart(val, fallbackToken) {
  if (val === null || val === undefined || val === '') return fallbackToken;
  return String(val);
}

/**
 * derivePositionRowKey — FULL identity key.
 *
 * When a strategy_instance_id (sii) is present:  `leg:sii:instrument:account`
 * When it is absent (broker-truth / BD rows):    `leg:broker:instrument:account`
 *
 * The BD (no-sii) branch reproduces the prior `broker:(instrument_id||symbol)` composite that
 * both _positionKey (Orders.jsx) and runReconcile keyed BD rows by — now leg-prefixed and
 * account-scoped for full-identity consistency — so this refactor is a provable no-op for
 * today's 100%-BD data shape (no BD row is dropped or re-collided).
 *
 * leg:        'ALGO' | 'MANUAL' | 'BD' (defaults 'BD' — today's only source that omits it)
 * instrument: pos.instrument_id, falling back to pos.symbol, else the token 'no-instrument'
 * account:    pos.account_id, else the token 'no-account' (BD rows never set account_id)
 *
 * @param {object|null|undefined} pos
 * @returns {string|null}
 */
export function derivePositionRowKey(pos) {
  if (!pos) return null;
  const leg = pos.leg || 'BD';
  const sii = pos.strategy_instance_id;
  const instrumentPart = degradeIdentityPart(pos.instrument_id || pos.symbol, 'no-instrument');
  const accountPart = degradeIdentityPart(pos.account_id, 'no-account');

  if (sii) {
    return `${leg}:${sii}:${instrumentPart}:${accountPart}`;
  }

  //* Degenerate (no sii) — broker-truth / BD rows. Compose leg:broker:instrument:account so
  //* two BD rows on different instruments never collide, and a BD row never collides with an
  //* ALGO row for the "same" instrument (different leg prefix). Mirrors the prior
  //* broker+instrument composite EXACTLY (with honest degradation), preserving today's behavior.
  if (instrumentPart !== 'no-instrument') {
    const brokerPart = degradeIdentityPart(pos.broker, 'no-broker');
    return `${leg}:${brokerPart}:${instrumentPart}:${accountPart}`;
  }

  //* Last resort for non-wire shapes carrying neither sii nor instrument: legacy ids.
  return pos.id || pos.position_id || pos.order_id || null;
}

/**
 * PositionsRegistry — wraps the Map stored in positionsRef.current. Maintains a secondary
 * sii → Set<fullKey> reverse index so BARE-sii callers (the WS lane only knows `sii` from its
 * topic name, `position.${sii}`) can resolve which full key(s) to touch without a linear scan.
 */
export class PositionsRegistry {
  constructor() {
    this._map = new Map();
    this._siiIndex = new Map();
  }

  /** Live row count (read-only convenience, not a mutation surface). */
  get size() {
    return this._map.size;
  }

  _indexAdd(sii, key) {
    if (!sii) return;
    if (!this._siiIndex.has(sii)) this._siiIndex.set(sii, new Set());
    this._siiIndex.get(sii).add(key);
  }

  _indexRemove(sii, key) {
    const s = sii && this._siiIndex.get(sii);
    if (!s) return;
    s.delete(key);
    if (s.size === 0) this._siiIndex.delete(sii);
  }

  /**
   * Upsert ONE row, merging onto any existing entry at the SAME full key (never a different
   * leg's entry). Returns the key, or null if the row has no derivable identity.
   * @param {object} pos
   * @returns {string|null}
   */
  upsert(pos) {
    let key = derivePositionRowKey(pos);
    if (!key) return null;
    //! #109 Live-Val 2026-09-08 — the SAME algo position arrived twice (REST consolidated row
    //!  with account_id + instrument UUID, WS position payload with neither) and rendered as
    //!  two rows. For sii rows the sii already pins user:strategy:version:instrument:tf, so a
    //!  missing account/instrument part is a WILDCARD, not a different position: merge into
    //!  the existing same-leg sii row when the account parts are equal or one is absent, and
    //!  keep the row under the FULLER key so later full-identity updates keep hitting it.
    const sii = pos.strategy_instance_id;
    if (sii) {
      const leg = pos.leg || 'BD';
      const myAcc = degradeIdentityPart(pos.account_id, 'no-account');
      const twin = this.getBySiiAndLeg(sii, leg).find((p) => {
        const acc = degradeIdentityPart(p.account_id, 'no-account');
        return acc === myAcc || acc === 'no-account' || myAcc === 'no-account';
      });
      if (twin) {
        const twinKey = derivePositionRowKey(twin);
        if (twinKey && twinKey !== key) {
          const merged = { ...twin, ...pos };
          //--> keep whichever identity is fuller (has a real account / instrument part)
          const fullerIsMine = myAcc !== 'no-account' && (pos.instrument_id || !twin.instrument_id);
          const keepKey = fullerIsMine ? key : twinKey;
          const dropKey = fullerIsMine ? twinKey : key;
          if (!fullerIsMine) {
            merged.account_id = twin.account_id ?? pos.account_id;
            merged.instrument_id = twin.instrument_id ?? pos.instrument_id;
          }
          this._map.delete(dropKey);
          this._indexRemove(sii, dropKey);
          this._map.set(keepKey, merged);
          this._indexAdd(sii, keepKey);
          return keepKey;
        }
      }
    }
    const existing = this._map.get(key);
    this._map.set(key, existing ? { ...existing, ...pos } : pos);
    this._indexAdd(sii, key);
    return key;
  }

  getByKey(key) {
    return this._map.get(key) || null;
  }

  deleteByKey(key) {
    const pos = this._map.get(key);
    if (pos) this._indexRemove(pos.strategy_instance_id, key);
    return this._map.delete(key);
  }

  /**
   * ALL rows (any leg/instrument/account) sharing a bare sii. Callers MUST handle 0/1/N —
   * never assume uniqueness (that assumption is exactly the CRITICAL-1/C2 collision root).
   * @param {string} sii
   * @returns {object[]}
   */
  getBySii(sii) {
    const keys = this._siiIndex.get(sii);
    if (!keys || keys.size === 0) return [];
    return Array.from(keys)
      .map((k) => this._map.get(k))
      .filter(Boolean);
  }

  /** Narrows getBySii to one leg — the shape the WS lane and algo-scoped surfaces need. */
  getBySiiAndLeg(sii, leg) {
    return this.getBySii(sii).filter((p) => (p.leg || 'BD') === leg);
  }

  /**
   * WS close-detection. Deletes every row for (sii, leg). Logs if >1 match — should never
   * happen for ALGO today (an ALGO sii is unique per instrument by construction), so >1 signals
   * either a backend WS-payload gap or a genuine multi-account same-instrument ALGO deployment.
   * delete-all favors "never leave a ghost position" over precision
   * (feedback_silent_failure_antipattern.md — loud + safe over silent).
   * @returns {number} count of rows deleted
   */
  deleteBySiiAndLeg(sii, leg) {
    const matches = this.getBySiiAndLeg(sii, leg);
    if (matches.length > 1) {
      // eslint-disable-next-line no-console
      console.warn(
        `[positionsRegistry] ambiguous WS close sii=${sii} leg=${leg} matches=${matches.length} — deleting all`,
      );
    }
    matches.forEach((p) => this.deleteByKey(derivePositionRowKey(p)));
    return matches.length;
  }

  /** All rows, any leg (the general, ALL-legs selector the Trades page needs). */
  values() {
    return Array.from(this._map.values());
  }

  /**
   * Scoped clear — clears ONLY rows of the given leg. Fixes a pre-existing blast-radius bug
   * (the algo-summary effect's bare `.clear()` was wiping BD/MANUAL rows too) as a byproduct
   * of migrating that site to the registry.
   */
  clearLeg(leg) {
    for (const [key, pos] of this._map.entries()) {
      if ((pos.leg || 'BD') === leg) {
        this._map.delete(key);
        this._indexRemove(pos.strategy_instance_id, key);
      }
    }
  }

  /** Full teardown clear — resets both the map and the reverse index (provider-unmount reset). */
  clear() {
    this._map.clear();
    this._siiIndex.clear();
  }
}
